/* NotificationsBell — admin bell wired to /api/admin/notifications (Phase G6).
   Polls unread-count every 30s, fetches list on open, navigates to the
   relevant gallery (Favorites tab for selection_submitted / overage_agreed),
   and exposes "mark all read".
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, X, Sparkles, Heart, UserPlus, CheckCircle2, Coins } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

function authHeaders() {
  const token = localStorage.getItem("allure_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const KIND_ICON = {
  collaborator_joined: UserPlus,
  collaborator_favorited: Heart,
  selection_submitted: CheckCircle2,
  overage_agreed: Coins,
};

export default function NotificationsBell({ "data-testid": testid = "admin-notifications-btn" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 24 });
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPos({ top: r.bottom + 12, right: Math.max(16, window.innerWidth - r.right) });
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications/unread-count`, { headers: authHeaders() });
      setUnread(Number(data?.count) || 0);
    } catch {
      // silent — bell is non-critical
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, { headers: authHeaders() });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count
  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, POLL_MS);
    return () => clearInterval(id);
  }, [fetchUnread]);

  // Position + load on open
  useEffect(() => {
    if (!open) return;
    reposition();
    fetchList();
    let raf = 0;
    const schedule = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; reposition(); }); };
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, { passive: true });
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open, reposition, fetchList]);

  useEffect(() => {
    function onClick(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const onRowClick = useCallback(async (n) => {
    // Mark this single notification read immediately
    try {
      await axios.post(`${API}/admin/notifications/mark-read`, { ids: [n.id] }, { headers: authHeaders() });
    } catch {}
    setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read_at: new Date().toISOString() } : it)));
    setUnread((c) => Math.max(0, c - (n.read_at ? 0 : 1)));
    setOpen(false);
    if (n.kind?.startsWith("inquiry")) {
      const id = n.payload?.inquiry_id;
      navigate(id ? `/admin/inquiries?open=${id}` : "/admin/inquiries");
    } else if (n.gallery_id) {
      const tabSuffix = n.kind === "selection_submitted" || n.kind === "overage_agreed" ? "?tab=favorites" : "";
      navigate(`/admin/galleries/${n.gallery_id}${tabSuffix}`);
    }
  }, [navigate]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-read`, { all: true }, { headers: authHeaders() });
      setItems((prev) => prev.map((it) => ({ ...it, read_at: it.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch {}
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="menu"
        className="relative w-10 h-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground border border-transparent hover:border-border rounded-sm transition-colors"
        data-testid={testid}
      >
        <Bell className="w-[17px] h-[17px]" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            data-testid="admin-notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-medium tracking-[0.02em] rounded-full"
            style={{ backgroundColor: "var(--gold-base, #C9A961)", color: "#0E0D0C" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="menu"
          data-testid="admin-notifications-panel"
          style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 1000, width: 360 }}
          className="bg-[color:var(--admin-panel-bg,#0F0E0D)] text-[color:var(--admin-panel-fg,#FAF9F6)] border border-[color:var(--gold-foil)]/30 shadow-[0_28px_90px_-15px_rgba(0,0,0,0.7)]"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--gold-foil)]/20">
            <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
              Notifications{unread > 0 ? ` · ${unread}` : ""}
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="w-6 h-6 inline-flex items-center justify-center rounded-full border border-[color:var(--gold-foil)]/30 text-current/70 hover:text-current hover:border-[color:var(--gold-base)] transition-colors"
              data-testid="admin-notifications-close"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto" data-testid="admin-notifications-list">
            {loading && (
              <div className="px-6 py-6 text-center text-[11px] uppercase tracking-[0.28em] text-current/40">Loading…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="px-6 py-8 text-center">
                <div className="mx-auto w-10 h-10 inline-flex items-center justify-center text-[color:var(--gold-base)] mb-3">
                  <Sparkles className="w-5 h-5" strokeWidth={1.25} />
                </div>
                <div className="font-serif italic text-xl text-current/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  All quiet on the wire.
                </div>
                <p className="mt-3 text-[11px] font-serif italic text-current/55 leading-relaxed max-w-[240px] mx-auto" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  You'll see new selections and overage agreements here.
                </p>
              </div>
            )}

            {!loading && items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const isUnread = !n.read_at;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => onRowClick(n)}
                  data-testid={`admin-notifications-row-${n.id}`}
                  className={`w-full text-left flex gap-3 px-5 py-3 border-b border-[color:var(--gold-foil)]/15 hover:bg-white/[0.04] transition-colors ${isUnread ? "" : "opacity-60"}`}
                >
                  <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-full border border-[color:var(--gold-foil)]/30 text-[color:var(--gold-base)]">
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[12.5px] leading-snug text-current/95">{n.title}</span>
                    <span className="block text-[11.5px] mt-0.5 text-current/55 truncate">{n.body}</span>
                    <span className="block text-[10px] mt-1 uppercase tracking-[0.22em] text-current/35">{relativeTime(n.created_at)}</span>
                  </span>
                  {isUnread && (
                    <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--gold-base, #C9A961)" }} aria-hidden />
                  )}
                </button>
              );
            })}
          </div>

          {!loading && items.length > 0 && (
            <div className="flex items-center justify-end px-5 py-3 border-t border-[color:var(--gold-foil)]/20">
              <button
                onClick={markAllRead}
                disabled={unread === 0}
                data-testid="admin-notifications-mark-all"
                className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] hover:text-current disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Mark all read
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
